/**
 * O NÚMERO AO VIVO DO HERO DE `/quotes` — a soma do recorte que está na tela.
 *
 * ─── POR QUE ISTO É UM MÓDULO PURO, E NÃO UM `reduce` NA PÁGINA ─────────────
 *
 * O desenho oficial do app põe um valor ao vivo no cabeçalho: em `/quotes/new`
 * é o total do documento, num cadastro virou o nome (#160), na home é o saldo
 * (#182/#187). Numa LISTA o análogo é quanto dinheiro está no recorte visível.
 *
 * E aí vale o corolário do #182: **um número no hero é uma AFIRMAÇÃO**, e se o
 * rótulo dele puder ser lido como outra coisa, o hero amplifica a mentira em
 * `text-3xl`. Somar tudo o que está listado seria exatamente isso — na aba
 * "Todos" entram os CANCELADOS e os REJEITADOS, que são dinheiro que não vai
 * entrar. Um total que os inclui diz ao prestador que ele tem mais na mão do
 * que tem.
 *
 * Então a soma exclui os dois status mortos — e, pelo "no silent caps" do repo
 * (o mesmo do `missingCostCount` em #61), quantos ficaram de fora é DEVOLVIDO
 * para a tela dizer. Cortar em silêncio é o que transforma um número honesto
 * num número menor sem explicação.
 *
 * A contagem, ao contrário, é de TODOS os documentos do recorte: ela responde
 * "quantos estou vendo", e tem que bater com a lista logo abaixo — se ela
 * também descontasse os cancelados, a linha "Selecionar todos (N)" e o hero
 * mostrariam números diferentes para a mesma tela.
 */
import type { Quote, QuoteStatus } from '@/types/database';

/** Status que NÃO representam dinheiro vivo. */
export const DEAD_STATUSES: ReadonlySet<string> = new Set<QuoteStatus>(['cancelled', 'rejected']);

/** `true` quando o recorte é EXATAMENTE um status morto (ver `includeDead`). */
export function isDeadStatusFilter(statusFilter: 'all' | QuoteStatus): boolean {
  return DEAD_STATUSES.has(statusFilter);
}

export interface QuoteListSummary {
  /** Documentos no recorte — todos, inclusive os cancelados/rejeitados. */
  count: number;
  /** Soma dos que ainda representam dinheiro (fora cancelados/rejeitados). */
  total: number;
  /** Quantos ficaram FORA da soma. Nunca cortado em silêncio (#61). */
  excludedCount: number;
}

/**
 * `money()` estrito de propósito, pelo mesmo motivo do `quoteTotals.ts` (#123a):
 * `total` chega como `number` do formulário, como STRING com ponto do PostgREST
 * (`numeric`) e como JSON da fila offline. `Number()` cru aceitaria `'0x10'` e
 * `true`; e uma string vazia viraria 0 em silêncio.
 */
export function quoteTotalNumber(raw: unknown): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  if (typeof raw !== 'string') return 0;
  const trimmed = raw.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return 0;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : 0;
}

export interface SummarizeOptions {
  /**
   * Soma TAMBÉM os cancelados/rejeitados.
   *
   * Existe por causa do filtro explícito por um status morto: com o recorte em
   * "Rejeitados", excluir todo mundo faria o hero anunciar **R$ 0,00** com uma
   * lista cheia embaixo — o número mais visível da tela contradizendo o que ela
   * mostra. A exclusão serve para a aba "Todos" não INFLAR o que o prestador
   * tem a receber; quando ele pede justamente os rejeitados, a pergunta mudou e
   * a resposta honesta é quanto se perdeu.
   */
  includeDead?: boolean;
}

/** Resumo do recorte JÁ FILTRADO (aba, busca e status aplicados pela tela). */
export function summarizeQuoteList(
  quotes: readonly Quote[],
  options: SummarizeOptions = {},
): QuoteListSummary {
  let total = 0;
  let excludedCount = 0;

  for (const quote of quotes) {
    if (!options.includeDead && DEAD_STATUSES.has(String(quote?.status))) {
      excludedCount += 1;
      continue;
    }
    total += quoteTotalNumber((quote as { total?: unknown })?.total);
  }

  return { count: quotes.length, total, excludedCount };
}

/**
 * O RÓTULO do valor — ele é o que torna o número honesto.
 *
 * Sem dizer de qual recorte a soma saiu, "R$ 12.400,00" sob o título
 * "Orçamentos" lê como "é isso que eu tenho", quando pode ser só a aba de
 * pendentes com uma busca por "João" ativa.
 */
export function quoteListTotalLabel(
  documentFilter: 'quotes' | 'sales',
  statusFilter: 'all' | QuoteStatus,
): string {
  if (documentFilter === 'sales') {
    return statusFilter === 'all' ? 'Total em vendas' : 'Total das vendas no filtro';
  }
  switch (statusFilter) {
    case 'pending':
      return 'Total pendente';
    case 'approved':
      return 'Total aprovado';
    case 'completed':
      return 'Total concluído';
    // Os dois status mortos ganham rótulo PRÓPRIO porque, sob o filtro deles, a
    // soma passa a incluí-los (`includeDead`): dizer "Total em orçamentos" ali
    // faria o número parecer receita quando ele é justamente o oposto.
    case 'rejected':
      return 'Total rejeitado';
    case 'cancelled':
      return 'Total cancelado';
    default:
      return 'Total em orçamentos';
  }
}
